import { LoadingSpinner, Tooltip } from "@jugl-web/ui-kit";
import { cx, useTranslations } from "@jugl-web/utils";
import React, { useMemo } from "react";

export const TrainingStatusPill: React.FC<{
  status?: "in_process" | "completed" | "need_retrain" | "processing";
  isFirstTraining?: boolean;
}> = ({ status, isFirstTraining }) => {
  const { t } = useTranslations();

  const { label, className, tooltip } = useMemo(() => {
    switch (status) {
      case "completed":
        return {
          label: t({
            id: "cx-ai-agents-page.trained",
            defaultMessage: "Trained",
          }),
          className: "border-secondary-100 bg-secondary-50",
          tooltip: undefined,
        };
      case "in_process":
        return {
          label: isFirstTraining
            ? t({
                id: "cx-ai-agents-page.training",
                defaultMessage: "Training...",
              })
            : t({
                id: "cx-ai-agents-page.retraining",
                defaultMessage: "Retraining...",
              }),
          className: "border-[#F1E9CA] bg-[#FAF7EC]",
          tooltip: undefined,
        };
      case "processing":
        return {
          label: t({
            id: "cx-ai-agents-page.processing",
            defaultMessage: "Processing...",
          }),
          className: "border-[#F1E9CA] bg-[#FAF7EC]",
          tooltip: undefined,
        };
      case "need_retrain":
        return {
          label: isFirstTraining
            ? t({
                id: "cx-ai-agents-page.to-be-trained",
                defaultMessage: "To be Trained",
              })
            : t({
                id: "cx-ai-agents-page.to-be-retrained",
                defaultMessage: "To be Retrained",
              }),
          className: "border-[#F1E9CA] bg-[#FAF7EC]",
          tooltip: isFirstTraining
            ? t({
                id: "cx-ai-agents-page.to-be-trained-tooltip",
                defaultMessage:
                  "Agent has not been trained yet. Train Agent to apply knowledge 🙌",
              })
            : t({
                id: "cx-ai-agents-page.to-be-retrained-tooltip",
                defaultMessage:
                  "Agent Knowledge was updated but not Retrained yet. Retrain Agent to apply updates 🙌",
              }),
        };
      default:
        return { label: "", className: "" };
    }
  }, [isFirstTraining, status, t]);

  if (!status) return null;
  return (
    <Tooltip
      isDisabled={!tooltip}
      placement="bottom"
      className="text-center"
      renderTrigger={({ props, ref }) => (
        <div
          className={cx(
            "flex h-7 cursor-default items-center justify-center gap-1.5 rounded-xl border border-solid px-2 font-secondary text-xs font-medium text-dark-800",
            className
          )}
          ref={ref}
          {...props}
        >
          {(status === "in_process" || status === "processing") && (
            <LoadingSpinner size="xs" color="grey" />
          )}
          <span>{label}</span>
        </div>
      )}
    >
      {tooltip}
    </Tooltip>
  );
};
